import { Gender, BodyCompositionType, ActivityLevel } from "./enums";
import { FitnessPlan } from "./data-model/fitness-plan";
import { TrainingLevel } from "./data-model/training-level";
import { BodyComposition, TrainingStatus } from "./plan-update-internals";
import { FitnessState, DeclaredState, isUsersTrainingGoalGrowth } from "./data-model/user-state";

/**
 * Estimates a body fat percentage at a new body weight, assuming lean body mass did not change.
 *
 * Takes a reference weight and the body fat percentage measured at that weight, works out the
 * lean mass those two imply, then re-derives body fat at the new weight from that same lean mass.
 *
 * This is the single copy of that rule. Two places use it, and they deliberately differ in what
 * they feed it and in what they do with the answer, which is why each keeps its own wrapper:
 * - estimateBFandLBM below, for the nutrition planning engine and the workout solver. It always
 *   uses the plan's start point as the reference, and falls back to the raw stored body fat only
 *   when the input was NaN.
 * - HomePage.getEstimatedBodyFat in client/src/pages/home/home.ts, for the goal card. It prefers
 *   the most recent measured weight/body-fat pair over the start point, and it discards an
 *   implausible answer (below 3% or above 60%) in favour of the raw stored body fat.
 */
export function estimateBFfromConstantLBM(referenceBW_kg: number, referenceBF_pc: number, estimatedBW_kg: number): number {
  const referenceLBM_kg = referenceBW_kg * (1 - referenceBF_pc / 100);
  return 100 - (referenceLBM_kg / estimatedBW_kg) * 100;
}

/**
 * Estimates $.estimatedBF_pc from start point and $.estimatedBW_kg
 */

export function estimateBFandLBM(fitnessPlan: FitnessPlan, fitnessState: FitnessState, estimatedBW_kg: number): number {
  let startPoint = fitnessPlan.startPoint;

  // if start point was initially incomplete (due to the OBSOLETE visual BF method), try to complete it now
  // (a plan with no start point skips this and falls back to the stored body fat below)
  if (startPoint && (!startPoint.BF_pc || (isNaN(startPoint.BF_pc) && !isNaN(fitnessState.bfData?.bf_pc)))) {
    startPoint.BF_pc = fitnessState.bfData?.bf_pc;
  }

  let estimatedBF_pc: number = undefined;
  if (startPoint && !isNaN(startPoint.BF_pc)) {
    estimatedBF_pc = estimateBFfromConstantLBM(startPoint.BW_kg, startPoint.BF_pc, estimatedBW_kg);
  }
  if (isNaN(estimatedBF_pc)) {
    estimatedBF_pc = fitnessState.bfData?.bf_pc;
  }

  return estimatedBF_pc;
}

// absorbs FP noise from estimateBFandLBM's BW × (1-BF/100) / BW × 100 round-trip,
// which produces e.g. 21.000000000000014 for an exact-21 BF input — without this,
// a user whose BF sits on a tier boundary silently falls one tier "fatter" (#3295).
export const BF_PC_BOUNDARY_EPSILON = 1e-9;

export function findBodyComposition(gender: Gender, estimatedBF_pc?: number) {
  if (estimatedBF_pc === undefined) {
    return undefined;
  }

  let bodyComposition: BodyComposition = undefined;
  if (gender === Gender.male) {
    bodyComposition = bodyCompositionTable.find(b => estimatedBF_pc <= b.maleBF_pc + BF_PC_BOUNDARY_EPSILON);
  } else {
    bodyComposition = bodyCompositionTable.find(b => estimatedBF_pc <= b.femaleBF_pc + BF_PC_BOUNDARY_EPSILON);
  }
  if (bodyComposition === undefined) {
    throw new Error('Invalid body composition');
  }

  return bodyComposition;
}

export function findEnergyBalanceFactor(declaredState: DeclaredState, fitnessState: FitnessState,
  gender: Gender, bodyComposition: BodyComposition, trainingStatus: TrainingStatus, estimatedBF_pc: number) {
  if (estimatedBF_pc) {
    if (isUsersTrainingGoalGrowth(declaredState.trainingGoalGrowth, gender, fitnessState.bfData.bf_pc)) {
      return trainingStatus.bulkingEBF;
    }
    return 1 - (bodyComposition.deficit_pc || 100) / 100;
  }

  return undefined;
}

export const bodyCompositionTable: BodyComposition[] = [
  {
    type: BodyCompositionType.Contest,
    maleBF_pc: 8,
    femaleBF_pc: 14,
    deficit_pc: 5,
    weeklyWeightLoss_pc: 0.5,
    TEF: 1.20,
    factorM: 11,
  },
  {
    type: BodyCompositionType.Athletic,
    maleBF_pc: 15,
    femaleBF_pc: 24,
    deficit_pc: 15,
    weeklyWeightLoss_pc: 0.7,
    TEF: 1.20,
    factorM: 11,
  },
  {
    type: BodyCompositionType.Average,
    maleBF_pc: 21,
    femaleBF_pc: 33,
    deficit_pc: 27.5,
    weeklyWeightLoss_pc: 1,
    TEF: 1.20,
    factorM: 11,
  },
  {
    type: BodyCompositionType.Overweight,
    maleBF_pc: 26,
    femaleBF_pc: 39,
    deficit_pc: 40,
    weeklyWeightLoss_pc: 1.5,
    TEF: 1.0,
    factorM: 9.7,
  },
  {
    type: BodyCompositionType.Obese,
    maleBF_pc: 100,
    femaleBF_pc: 100,
    deficit_pc: 50, // null means PSMF
    weeklyWeightLoss_pc: 100, // means unlimited (not that this will actually happen literally)
    TEF: 1.0,
    factorM: 8.3,
  },
];
export const physicalActivityTable = {
  [Gender.male]: {
    [ActivityLevel.Sedentary]: 1,
    [ActivityLevel.SomewhatActive]: 1.11,
    [ActivityLevel.Active]: 1.25,
    [ActivityLevel.VeryActive]: 1.48,
  },
  [Gender.female]: {
    [ActivityLevel.Sedentary]: 1,
    [ActivityLevel.SomewhatActive]: 1.12,
    [ActivityLevel.Active]: 1.27,
    [ActivityLevel.VeryActive]: 1.45,
  },
};

export const trainingStatusTable: TrainingStatus[] = [
  {
    level: TrainingLevel.Novice,
    bulkingEBF: 1.1,
    weeklyWeightGain_pc: 1.0,
    anabolicMeals: 8,
  },
  {
    level: TrainingLevel.Intermediate,
    bulkingEBF: 1.045,
    weeklyWeightGain_pc: 0.5,
    anabolicMeals: 4,
  },
  {
    level: TrainingLevel.Advanced,
    bulkingEBF: 1.02,
    weeklyWeightGain_pc: 0.14,
    anabolicMeals: 3,
  },
];

/**
 * Whether this plan's daily calorie target is a number the USER chose rather than the one the
 * solver worked out, and which number that is.
 *
 * "The user chose it" has two shapes, because a typed target is not applied by the solver
 * immediately — and sometimes not at all (`cybsol/mh-physique#5181`):
 *
 * - `overrideDailyAverageIntake` — what was on the Edit Daily Nutrition Goal screen when it
 *   was last saved. Since #5181 the solver puts one into force on every path that has body
 *   data to work from AND a recommendation of its own to replace, so it is normally
 *   short-lived. A request can still outlive a solve — a plan saved before that fix, a solve
 *   with no bodyweight or body-fat reading, a solve that produced no target of its own — so
 *   this helper has to recognise the parked shape rather than treat it as legacy. It also
 *   runs before any solve cleans it. Note
 *   the screen writes this field on EVERY save, even when only protein or fibre was edited,
 *   so it can hold the solver's own number — which is why a pending value only counts when it
 *   actually differs from the live target.
 * - `baselineNominalDailyAverageIntake` — written only once a typed target HAS been applied,
 *   holding the number it replaced. Its presence means the live target is the user's.
 *
 * `wasOverriddenManually` looks like a third candidate and is not: `adjustForProgress` clears
 * it at the top of every ordinary solve.
 *
 * The kcal figure is the pending number when that number is a real choice, and the live target
 * otherwise — deliberately NOT the plain `override ?? actual` order the nutrition card and the
 * edit screen display, because those would show a parked zero or a value that merely repeats
 * the live target. A PENDING figure has not been through the solver's minimum-intake clamp, so
 * it can sit below that floor.
 *
 * Full rules: design-docs/nutrition-target-persistence.md.
 */
export function readUsersOwnCalorieTarget(fitnessPlan: FitnessPlan): { hasOwnTarget: boolean, target_kcal?: number } {
  const pending = fitnessPlan?.overrideDailyAverageIntake;
  const applied = fitnessPlan?.actualDailyAverageIntake;
  // A pending value equal to the live target is what the screen saved back untouched, not a
  // choice — rounded, because the two travel through different arithmetic.
  //
  // It must also be a number somebody could eat to. The edit screen accepts a typed 0, and
  // the solver refuses to put a non-positive target into force and now discards the request
  // (`discardUnusableIntakeRequest` in plan-update.ts, same `> 0` test). A plan saved before
  // that discard existed can still carry a parked 0, and this runs BEFORE any solve cleans
  // it — so without this term a screen would announce "your daily calories stay at 0 kcal"
  // and call it the user's own target.
  const pendingIsAChoice = pending != null && pending > 0
    && (applied == null || Math.round(pending) !== Math.round(applied));
  const hasOwnTarget = pendingIsAChoice || fitnessPlan?.baselineNominalDailyAverageIntake != null;
  if (!hasOwnTarget) {
    return { hasOwnTarget: false };
  }
  // `pending ?? applied` would be wrong here: `??` skips only null and undefined, so a parked
  // zero would pass straight through as the number to show — the exact "0 kcal for ever"
  // display this rule exists to keep out. Name the pending number only when it is a choice.
  return { hasOwnTarget: true, target_kcal: pendingIsAChoice ? pending : applied };
}
